import { useEffect } from "react";
import { toast } from "sonner";

import { AuthenticationStatus, AuthQuery, useAuthStore } from "../model";
import type { Service } from "../model/service";

import type { EventBus } from "~/entities/event-bus/@x/auth";
import { Tk, useTranslation2 } from "~/shared/i18n";

type Props = {
  authService: Service;
  eventBus: EventBus;
};

function AuthResolver({ authService, eventBus }: Props) {
  const {
    postproc: { tc },
  } = useTranslation2();

  const { data: me, isError: isMeError } = AuthQuery.useGetMe(authService, {
    retry: false,
  });

  const status = useAuthStore.use.status();
  const loginLocally = useAuthStore.use.login();
  const logoutLocally = useAuthStore.use.logout();

  useEffect(() => {
    if (!(me || isMeError)) return;

    if (me) {
      loginLocally(me.roles);
    } else if (isMeError) {
      logoutLocally();
    }
  }, [isMeError, loginLocally, logoutLocally, me]);

  useEffect(() => {
    const handleUnauth = () => {
      if (status !== AuthenticationStatus.AUTHENTICATED) return;

      logoutLocally();
      toast.info(tc(Tk.AUTHENTICATION_SESSION_EXPIRED));
    };

    const params = ["userNotAuthenticated", handleUnauth] as const;
    eventBus.on(...params);

    return () => {
      eventBus.off(...params);
    };
  }, [eventBus, logoutLocally, status, tc]);

  return null;
}

export { AuthResolver };
